import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Autocomplete,
    Typography,
    Box,
    Divider,
    FormControlLabel,
    Switch,
    Grid,
    Alert
} from '@mui/material';
import MerchantViewCard from './MerchantViewCard';
import { useGetMerchants } from '../../api/merchants';

const MerchantEditDialog = ({
    open,
    onClose,
    onSave,
    merchant = null,
    title = "Edit Merchant"
}) => {
    const [createNewMode, setCreateNewMode] = useState(false);
    const [selectedMerchant, setSelectedMerchant] = useState(null);
    const {data: merchantList, isLoading: isMerchantListLoading, error: isMerchantListError} = useGetMerchants();

    const mapMerchantRecord = useCallback((merchant) => ({
        merchantID: merchant?.id || null,
        merchantName: merchant?.name || null,
        merchantWebsite: merchant?.website || null,
        merchantPhone: merchant?.phone || null,
        merchantStreetAddress: merchant?.street_address || null,
        merchantCity: merchant?.city || null,
        merchantState: merchant?.state || null,
        merchantZip: merchant?.zip || null,
        merchantGeocode: merchant?.geocode || null,
        hasMerchantMapData: function () {
            return !!this.merchantGeocode;
        },
    }), []);

    // Memoize the formatted merchant list to prevent unnecessary recalculations
    const formattedMerchantList = useMemo(() =>
        merchantList?.map(mapMerchantRecord) || [],
        [merchantList, mapMerchantRecord]
    );

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors, isDirty, dirtyFields }
    } = useForm({
        defaultValues: {
            merchantName: '',
            merchantWebsite: '',
            merchantPhone: '',
            merchantStreetAddress: '',
            merchantCity: '',
            merchantState: '',
            //merchantLocation: '',
            merchantZip: ''
        }
    });

    // Watch specific fields instead of all values to reduce re-renders
    const watchedValues = watch([
        'merchantName',
        'merchantWebsite',
        'merchantPhone',
        'merchantStreetAddress',
        'merchantCity',
        'merchantState',
        //'merchantLocation',
        'merchantZip'
    ]);

    // Memoize preview merchant to prevent unnecessary recalculations
    const previewMerchant = useMemo(() => {
        if (!createNewMode && !selectedMerchant) {
            return null;
        }

        const baseMerchant = selectedMerchant || {};
        const [
            merchantName,
            merchantWebsite,
            merchantPhone,
            merchantStreetAddress,
            merchantCity,
            merchantState,
            // merchantLocation,
            merchantZip
        ] = watchedValues;

        return {
            ...baseMerchant,
            merchantName,
            merchantWebsite,
            merchantPhone,
            merchantStreetAddress,
            merchantCity,
            merchantState,
            // merchantLocation,
            merchantZip,
            hasMerchantMapData: () => baseMerchant.hasMerchantMapData?.() || false,
            merchantGeocode: baseMerchant.merchantGeocode || null
        };
    }, [watchedValues, selectedMerchant, createNewMode]);

    // Initialize form when dialog opens or merchant changes
    useEffect(() => {
        if (!open) return;

        if (merchant) {
            // Editing existing merchant
            reset({
                merchantName: merchant.merchantName || '',
                merchantWebsite: merchant.merchantWebsite || '',
                merchantPhone: merchant.merchantPhone || '',
                merchantStreetAddress: merchant.merchantStreetAddress || '',
                merchantCity: merchant.merchantCity || '',
                merchantState: merchant.merchantState || '',
                // merchantLocation: merchant.merchantLocation || '',
                merchantZip: merchant.merchantZip || ''
            });
            setSelectedMerchant(merchant);
            setCreateNewMode(false);
        } else {
            // Creating new merchant
            reset({
                merchantName: '',
                merchantWebsite: '',
                merchantPhone: '',
                merchantStreetAddress: '',
                merchantCity: '',
                merchantState: '',
                // merchantLocation: '',
                merchantZip: ''
            });
            setSelectedMerchant(null);
            setCreateNewMode(true);
        }
    }, [open, merchant, reset]);

    const handleMerchantSelect = useCallback((event, newValue) => {
        if (newValue) {
            setSelectedMerchant(newValue);
            reset({
                merchantName: newValue.merchantName || '',
                merchantWebsite: newValue.merchantWebsite || '',
                merchantPhone: newValue.merchantPhone || '',
                merchantStreetAddress: newValue.merchantStreetAddress || '',
                merchantCity: newValue.merchantCity || '',
                merchantState: newValue.merchantState || '',
                // merchantLocation: newValue.merchantLocation || '',
                merchantZip: newValue.merchantZip || ''
            });
            setCreateNewMode(false);
        } else {
            setSelectedMerchant(null);
            setCreateNewMode(true);
        }
    }, [reset]);

    const handleCreateNewToggle = useCallback((event) => {
        const isCreateNew = event.target.checked;
        setCreateNewMode(isCreateNew);

        if (isCreateNew) {
            setSelectedMerchant(null);
            reset({
                merchantName: '',
                merchantWebsite: '',
                merchantPhone: '',
                merchantStreetAddress: '',
                merchantCity: '',
                merchantState: '',
                // merchantLocation: '',
                merchantZip: ''
            });
        }
    }, [reset]);

    const onSubmit = useCallback((data) => {
        // Only send fields the user actually touched. The :Merchant node is shared
        // by every transaction pointing at it, so submitting an untouched blank
        // used to wipe a stored value (e.g. website) case-wide. merchantName is
        // the merchant identity, so it is always sent; a field the user
        // deliberately cleared is dirty and still sent (as ''), which clears it.
        const editedFields = Object.fromEntries(
            Object.entries(data).filter(([field]) => field === 'merchantName' || dirtyFields[field])
        );

        const merchantData = {
            ...editedFields,
            // Include original merchant data if editing existing
            ...(selectedMerchant && !createNewMode ? {
                id: selectedMerchant.id,
                merchantGeocode: selectedMerchant.merchantGeocode,
                hasMerchantMapData: selectedMerchant.hasMerchantMapData
            } : {}),
            isNew: createNewMode
        };

        onSave(merchantData);
        onClose();
    }, [selectedMerchant, createNewMode, onSave, onClose, dirtyFields]);

    // Unlink the merchant from the transaction(s) without touching the shared
    // :Merchant node. An explicit isRemoval flag rather than "the user blanked
    // the name field" so intent is unambiguous at every layer below this one.
    const onRemove = useCallback(() => {
        onSave({ isRemoval: true });
        onClose();
    }, [onSave, onClose]);

    const handleClose = useCallback(() => {
        reset();
        setSelectedMerchant(null);
        setCreateNewMode(false);
        onClose();
    }, [reset, onClose]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { minHeight: '70vh' }
            }}
        >
            <DialogTitle>
                {title}
            </DialogTitle>

            <DialogContent dividers>
                <Box component="form" noValidate>
                    {/* Merchant Selection Section */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Select Merchant
                        </Typography>

                        <Autocomplete
                            options={formattedMerchantList}
                            getOptionLabel={(option) => option?.merchantName || ''}
                            isOptionEqualToValue={(option, value) => option?.merchantID === value?.merchantID}
                            value={selectedMerchant}
                            onChange={handleMerchantSelect}
                            disabled={createNewMode}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Search existing merchants"
                                    variant="outlined"
                                    fullWidth
                                    placeholder="Type to search merchants..."
                                />
                            )}
                            renderOption={(props, option) => {
                                const { key, ...otherProps } = props;
                                return (
                                    <Box component="li" key={key} {...otherProps}>
                                        <Box>
                                            <Typography variant="body1">
                                                {option?.merchantName || ''}
                                            </Typography>
                                            {option?.merchantCity && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {option?.merchantCity} {option?.merchantState ? `, ${option.merchantState}` : ''}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            }}
                            sx={{ mb: 2 }}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={createNewMode}
                                    onChange={handleCreateNewToggle}
                                />
                            }
                            label="Create new merchant"
                        />
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Merchant Details Form */}
                    <Typography variant="h6" gutterBottom>
                        Merchant Details
                    </Typography>

                    {!createNewMode && !selectedMerchant && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Select a merchant above to edit its details, or toggle "Create new merchant" to add a new one.
                        </Alert>
                    )}

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="merchantName"
                                control={control}
                                rules={{
                                    required: createNewMode ? 'Merchant name is required' : false
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Merchant Name"
                                        fullWidth
                                        error={!!errors.merchantName}
                                        helperText={errors.merchantName?.message}
                                        disabled={!createNewMode && !selectedMerchant}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="merchantWebsite"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Website"
                                        fullWidth
                                        type="url"
                                        disabled={!createNewMode && !selectedMerchant}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="merchantPhone"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Phone"
                                        fullWidth
                                        type="tel"
                                        disabled={!createNewMode && !selectedMerchant}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="merchantStreetAddress"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Street Address"
                                        fullWidth
                                        disabled={!createNewMode && !selectedMerchant}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Controller
                                name="merchantCity"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="City"
                                        fullWidth
                                        disabled={!createNewMode && !selectedMerchant}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Controller
                                name="merchantState"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="State/Province (incl. Country)"
                                        fullWidth
                                        disabled={!createNewMode && !selectedMerchant}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Controller
                                name="merchantZip"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="ZIP Code"
                                        fullWidth
                                        disabled={!createNewMode && !selectedMerchant}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    {/* Preview Section */}
                    {previewMerchant && (
                        <>
                            <Divider sx={{ my: 3 }} />
                            <Typography variant="h6" gutterBottom>
                                Preview
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <Grid container>
                                    <MerchantViewCard merchant={previewMerchant} />
                                </Grid>
                            </Box>
                        </>
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                {/* Only offered when a merchant is actually assigned. Gating on
                    merchantName rather than the selection itself: the viewer
                    opens this dialog with a populated object even for a
                    transaction that has no merchant, where there is nothing to
                    unlink. */}
                {!createNewMode && selectedMerchant?.merchantName && (
                    <Button
                        onClick={onRemove}
                        color="error"
                        sx={{ mr: 'auto' }}
                    >
                        Remove Merchant
                    </Button>
                )}
                <Button onClick={handleClose}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit(onSubmit)}
                    variant="contained"
                    disabled={!createNewMode && !selectedMerchant}
                >
                    {createNewMode ? 'Create Merchant' : 'Update Merchant'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MerchantEditDialog;